import { useForm, SubmitHandler } from 'react-hook-form';
import { Input, Button, FormControl, FormLabel, Link, Box, useToast, Text, Skeleton } from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from 'axios';
import { UIErrorResponse } from 'types/src/error';
import { useDomainConfig } from '../../../../hooks/useDomainConfig';
import { useUser } from '../../../../hooks/V2/useUser';
import { signIn, verifySignInOtp } from '../../../../apis/V2/auth';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import OtpPage from './OtpPage';
import { ErrorResponse } from '../../../Auth/SignUpForm';
import { sendTrackingEvent } from '../../../../utils/gtm';
import { useVerifyExternalToken } from '../../../../hooks/useVerifyExternalToken';
import { SAGEX_ORG_KEY } from '../../../../utils/segexOrgName';

interface SignInFormInputs {
	phoneOrEmail?: string | undefined;
}

interface SignInFormProps {
	onSignUpClick?: () => void;
}

let isVerifyingByPhone = true;

export const nameRegex = /^[a-zA-Z]+(?: [a-zA-Z]+)*$/;
export const phoneRegex = /^[6-9]\d{9}$/;
export const emailRegex =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SignInForm = ({ onSignUpClick }: SignInFormProps) => {
	// Verify external token
	const location = useLocation();
	// Parse the query parameters to get the token
	const queryParams = new URLSearchParams(location.search);
	const token = queryParams.get('token');
	// Use the useVerifyExternalToken hook
	const { data: verifyTokenData } = useVerifyExternalToken(token || '');
	const toast = useToast();
	const navigate = useNavigate();
	const [otp, setOtp] = useState<string>('');
	const [showOtpUi, setShowOtpUi] = useState(false);
	const { isLoading, isError, data: domainConfig } = useDomainConfig({ uiHost: window.location.host });
	const [phoneOrEmail, setPhoneOrEmail] = useState('');
	const { data, refetch } = useUser();

	useEffect(() => {
		if (data) {
			navigate('/dashboard');
		}
	}, [data]);

	const schema = yup
		.object({
			phoneOrEmail: yup
				.string()
				.test('no_country_code', 'Phone number should not contain +91 and should be 10 digit long', value => {
					if (value && isVerifyingByPhone) {
						// Check if the phone number contains +91
						return !value.includes('+91');
					}
					return true;
				})
				.test('phone_or_email', 'Email or Phone is invalid', value => {
					if (!value) return false;
					if (emailRegex.test(value)) {
						isVerifyingByPhone = false;
						return true;
					}
					if (phoneRegex.test(value)) {
						isVerifyingByPhone = true;
						return true;
					}
					return false;
				}),
		})
		.required();

	const { register, handleSubmit, formState, setValue } = useForm<SignInFormInputs>({
		resolver: yupResolver(schema),
	});

	useEffect(() => {
		if (domainConfig?.key === SAGEX_ORG_KEY) {
			if (verifyTokenData) {
				setValue('phoneOrEmail', verifyTokenData?.phoneNumber || verifyTokenData?.email);
				setShowOtpUi(true);
				setPhoneOrEmail(verifyTokenData?.phoneNumber || verifyTokenData?.email);
				onSubmit({ phoneOrEmail: verifyTokenData?.phoneNumber || verifyTokenData?.email });
			} else {
				window.location.href = 'http://sageextension.com/login';
			}
		}
	}, [verifyTokenData, domainConfig?.key, setValue]);
	useEffect(() => {
		setOtp('');
	}, [showOtpUi]);

	useEffect(() => {
		if (otp.length === 6) {
			handleVerifySignInOtp();
		}
	}, [otp]);

	const { mutate: verifySignInOtpMutate, isLoading: verifySignInOtpLoading } = useMutation(verifySignInOtp, {
		onSuccess: () => {
			// refetch user
			refetch();
			setOtp('');
			showOtpUi && setShowOtpUi(false);
			sendTrackingEvent({
				event: 'cn_signin_verify',
				eventLabel: window.location.href,
			});
			navigate('/dashboard');
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'Otp verification failed!',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});

	const { mutate: signInMutate, isLoading: isSignInLoading } = useMutation(signIn, {
		onSuccess: res => {
			setShowOtpUi(true);
		},
		onError: (error: AxiosError<UIErrorResponse>) => {
			toast({
				title: 'Error',
				description: error?.response?.data?.message || 'User signin failed',
				status: 'error',
				duration: 2000,
				isClosable: true,
				position: 'top',
			});
		},
	});

	if (isLoading) {
		return <Skeleton h="100vh">Loading...</Skeleton>;
	}
	if (isError) {
		return <div>Error</div>;
	}

	const onSubmit: SubmitHandler<SignInFormInputs> = data => {
		// call sign in api
		// redirect to dashboard
		console.log('isVerifyingByPhone:', isVerifyingByPhone);
		setPhoneOrEmail(data?.phoneOrEmail || '');
		signInMutate({
			email: isVerifyingByPhone ? '' : data?.phoneOrEmail,
			phone: isVerifyingByPhone ? data?.phoneOrEmail : '',
			domainKey: domainConfig?.key,
		});
	};

	const handleVerifySignInOtp = () => {
		verifySignInOtpMutate({
			email: isVerifyingByPhone ? '' : phoneOrEmail || '',
			phone: isVerifyingByPhone ? phoneOrEmail || '' : '',
			otp: otp,
		});
	};

	return (
		<>
			{!showOtpUi && (
				<Box as="form" w="full" onSubmit={handleSubmit(onSubmit)}>
					<Text textStyle={{ base: 'h3', md: 'h2' }} textAlign="center" my="30px">
						Sign In
					</Text>

					<FormControl id="phoneOrEmail" mb="24px" isInvalid={formState.errors.phoneOrEmail ? true : false}>
						<Text as={FormLabel} textStyle="body2-md">
							Phone number or email address *
						</Text>
						<Input
							autoFocus={true}
							placeholder="Enter phone number or email address"
							{...register('phoneOrEmail', { required: 'Email or Phone is invalid', setValueAs: v => v.trim().toLowerCase() })}
						/>
						{formState.errors.phoneOrEmail && (
							<Text textStyle="body2" mt="6px" color="#D61E27">
								{formState.errors.phoneOrEmail.message}
							</Text>
						)}
					</FormControl>

					<Box display={'flex'} gap="8px" alignItems={'center'}>
						<Button
							size="lg"
							textStyle="btn-lg"
							w="full"
							variant="primary"
							bg="#4358F6"
							type="submit"
							isLoading={isSignInLoading}
							onClick={() => {
								sendTrackingEvent({
									event: 'cn_signin_signin',
									eventLabel: window.location.href,
								});
							}}
						>
							Sign In
						</Button>
					</Box>

					<Box display="flex" gap="8px" color="#544D4F" textStyle="body1-md" justifyContent="center" textAlign="center" mt="40px" flexWrap="wrap">
						Don't have an account?
						<Link
							onClick={e => {
								sendTrackingEvent({
									event: 'cn_signin_signup',
									eventLabel: window.location.href,
								});
								if (onSignUpClick) {
									e.preventDefault();
									onSignUpClick();
								}
							}}
							as={RouterLink}
							color="#386AFA"
							to="/sign-up"
						>
							Sign Up
						</Link>
					</Box>
				</Box>
			)}

			{showOtpUi && phoneOrEmail && (
				<Box
					as="form"
					maxW="343px"
					mx="auto"
					onSubmit={e => {
						e.preventDefault();
						handleVerifySignInOtp();
					}}
				>
					<OtpPage phoneNumber={phoneOrEmail} setShowOtpUi={setShowOtpUi} otp={otp} setOtp={setOtp} resendOtp={handleSubmit(onSubmit)} />

					<Box mt="20px" display={'flex'} gap="8px" alignItems={'center'}>
						<Button
							type="submit"
							size="lg"
							textStyle="btn-lg"
							w="full"
							variant="primary"
							bg="#4358F6"
							isLoading={verifySignInOtpLoading}
							onClick={() => {
								sendTrackingEvent({
									event: 'cn_signin_verify',
									eventLabel: window.location.href,
								});
							}}
						>
							Verify
						</Button>
					</Box>
				</Box>
			)}
		</>
	);
};

export default SignInForm;
